import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BaseLayout } from '@/components/layout';
import { useDrones } from '../hooks/useDrones';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

const DroneViewer: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: dronesData, isLoading, error } = useDrones(projectId);
  
  const [selectedDroneType, setSelectedDroneType] = useState<string>('');
  const [selectedDroneName, setSelectedDroneName] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [iframeUrl, setIframeUrl] = useState<string>('https://ftp.ironview360.com/templates/Loading/#media=loading-screen');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);

  // Set default selections when data loads
  useEffect(() => {
    if (dronesData && dronesData.drone_types.length > 0) {
      const firstType = dronesData.drone_types[0];
      setSelectedDroneType(firstType);
      
      const firstDroneName = Object.keys(dronesData.project_drones[firstType])[0];
      if (firstDroneName) {
        setSelectedDroneName(firstDroneName);
        const firstDate = dronesData.project_drones[firstType][firstDroneName]?.dates[0];
        if (firstDate) {
          setSelectedDate(firstDate);
          const videoInfo = dronesData.project_drones[firstType][firstDroneName].videos.find(v => v.date === firstDate);
          if (videoInfo) setIframeUrl(videoInfo.index_url);
        }
      }
    }
  }, [dronesData]);

  const handleDateClick = (droneName: string, date: string, droneType: string) => {
    if (!dronesData) return;
    const droneData = dronesData.project_drones[droneType]?.[droneName];
    if (!droneData) return;
    const videoInfo = droneData.videos.find(v => v.date === date);
    if (!videoInfo) return;
    setSelectedDroneName(droneName);
    setSelectedDate(date);
    setIframeUrl(videoInfo.index_url);
  };

  const handleCardClick = (droneName: string, droneType: string) => {
    if (!dronesData) return;
    const droneData = dronesData.project_drones[droneType]?.[droneName];
    if (!droneData || droneData.dates.length === 0) return;
    // If clicking the already-selected drone, do nothing
    if (selectedDroneName === droneName) return;
    // Load the first date of the clicked drone
    const firstDate = droneData.dates[0];
    handleDateClick(droneName, firstDate, droneType);
  };

  // Handle all non-success states with BaseLayout
  if (isLoading || error || !dronesData || dronesData.drone_types.length === 0) {
    return (
      <BaseLayout 
        fullHeight={true}
        isLoading={isLoading}
        loadingMessage="Loading drone videos..."
        loadingSize="xl"
      >
        {error && (
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-destructive">Error loading drone data</h2>
              <p className="text-muted-foreground mt-2">Please try again later</p>
            </div>
          </div>
        )}
        {!isLoading && dronesData && dronesData.drone_types.length === 0 && (
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-foreground">No drone data available</h2>
              <p className="text-muted-foreground mt-2">No drone tours found for this project</p>
            </div>
          </div>
        )}
      </BaseLayout>
    );
  }

  return (
    <BaseLayout fullHeight={true}>
      <div className="h-[calc(100vh-4rem)] w-full bg-background flex overflow-hidden">
        {/* Main Content Area */}
        <div className="flex-1 flex relative">
          {/* Sidebar */}
          <div 
            className={`bg-card border-r border-border flex flex-col transition-all duration-300 ease-in-out h-full ${
              isSidebarCollapsed ? 'w-0' : 'w-80'
            }`}
          >
            <div className={`flex flex-col h-full ${isSidebarCollapsed ? 'hidden' : 'flex'}`}>
              <div className="p-4 border-b border-border flex-shrink-0">
                <h1 className="text-xl font-semibold text-foreground">{dronesData.project.name}</h1>
                <p className="text-sm text-muted-foreground">Drone Tours</p>
              </div>
              
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <Tabs value={selectedDroneType} onValueChange={setSelectedDroneType} className="w-full h-full flex flex-col">
                  <div className="overflow-x-auto mx-4 mt-4 flex-shrink-0">
                    <TabsList className="inline-flex w-auto">
                      {dronesData.drone_types.map((droneType) => (
                        <TabsTrigger key={droneType} value={droneType} className="text-xs whitespace-nowrap">
                          {droneType}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>

                  {dronesData.drone_types.map((droneType) => (
                    <TabsContent key={droneType} value={droneType} className="mt-4 space-y-2 px-4 pb-4 flex-1 overflow-y-auto">
                      {Object.entries(dronesData.project_drones[droneType]).map(([droneName, droneData]) => (
                        <Card 
                          key={droneName} 
                          className={`cursor-pointer transition-all duration-200 ${
                            selectedDroneName === droneName
                              ? 'bg-primary border-primary/50 shadow-md ring-1 ring-primary/30'
                              : 'hover:bg-accent/50 hover:border-border'
                          }`}
                          onClick={() => handleCardClick(droneName, droneType)}
                        >
                          <CardContent className="p-3">
                            <h4 className={`font-semibold text-sm mb-2 ${
                              selectedDroneName === droneName ? 'text-primary-foreground' : 'text-foreground'
                            }`}>
                              {droneName}
                            </h4>
                            <div className="flex flex-wrap gap-1">
                              {droneData.dates.map((date, idx) => (
                                <Button
                                  key={`${date}-${idx}`}
                                  size="sm"
                                  variant={selectedDroneName === droneName && selectedDate === date ? 'default' : 'outline'}
                                  className={`text-xs h-6 ${
                                    selectedDroneName === droneName && selectedDate === date
                                      ? 'bg-primary-foreground text-primary hover:bg-primary-foreground/90'
                                      : selectedDroneName === droneName
                                        ? 'border-primary/30 text-primary-foreground hover:bg-primary/80'
                                        : ''
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDateClick(droneName, date, droneType);
                                  }}
                                >
                                  {date}
                                  <ExternalLink className="w-3 h-3 ml-1" />
                                </Button>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </div>
          </div>

          {/* Toggle Button */}
          <Button
  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
  className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 h-12 w-10 rounded-r-lg rounded-l-none bg-card border border-border shadow-lg hover:bg-accent transition-all duration-200 flex items-center justify-center"
  style={{
    left: isSidebarCollapsed ? '0px' : '320px',
    transition: 'left 0.3s ease-in-out'
  }}
  variant="outline"
  size="sm"
>
  {isSidebarCollapsed ? (
    <ChevronRight className="h-6 w-6 text-muted-foreground" />
  ) : (
    <ChevronLeft className="h-6 w-6 text-muted-foreground" />
  )}
</Button>




          {/* Main Viewer */}
          <div className="flex-1 relative">
            <iframe
              src={iframeUrl}
              className="w-full h-full border-none"
              allowFullScreen
              title="Drone Tour Viewer"
              allow="vr; xr; accelerometer; magnetometer; gyroscope; webvr; webxr; xr-spatial-tracking; autoplay; camera; microphone"
            />
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

export default DroneViewer; 